// The customer's signature accepting the work (QA-727): who signed and when, where, and the pad, on
// the job, its invoice and the customer's history. The pad's link is the server's short-lived one.
// Blueprint (BL-189): under the catalogued Section, its name an h3 in the sheet whose title is the h2.
import { useId } from 'react';
import { Section } from '@flowology/ui-web/patterns';
import { useDesign } from '../blueprint/design.ts';
import { PAD_ON_ITS_WAY, signedWhere, signedWords, type AcceptanceSignature } from './signed-model.ts';
import './signed.css';

/** One signature's pad: the picture, or why it isn't here yet. */
export function SignaturePad({
  s,
  small = false,
}: {
  s: Pick<AcceptanceSignature, 'signerName' | 'imageUrl'>;
  small?: boolean;
}) {
  return s.imageUrl ? (
    <img
      className={`signed-pad${small ? ' sm' : ''}`}
      src={s.imageUrl}
      alt={`${s.signerName}’s signature`}
      loading="lazy"
    />
  ) : (
    <p className="muted small">{PAD_ON_ITS_WAY}</p>
  );
}

/** The signatures on a job or an invoice; nothing at all when there are none. */
export function SignedForWork({
  signatures,
  timeZone,
  withInvoice = false,
}: {
  signatures: readonly AcceptanceSignature[];
  timeZone: string;
  /** Say which invoice each is on (a job's sheet). */
  withInvoice?: boolean;
}) {
  const title = useId();
  const bp = useDesign() === 'blueprint';
  if (!signatures.length) return null;
  const list = (
    <ul className="signed-list">
      {signatures.map((s) => (
        <li key={s.signatureId}>
          <p>{signedWords(s, timeZone)}</p>
          <p className="muted small">{signedWhere(s, withInvoice)}</p>
          <SignaturePad s={s} />
        </li>
      ))}
    </ul>
  );
  if (bp)
    return (
      <Section title="Customer’s signature" headingId={title} level={3}>
        {list}
      </Section>
    );
  return (
    <section className="job-sect signed-work" aria-labelledby={title}>
      <h3 id={title}>Customer’s signature</h3>
      {list}
    </section>
  );
}
